import type { MarketSelectorPreset } from '@src/components/MarketSelector/types';
import { OrderPresetSearchSelect } from '@src/components/OrderPresets/OrderPresetSearchSelect';
import { SubAccountSearchSelect } from '@src/components/SubAccounts/SubAccountSearchSelect';
import type { IOrderPreset } from '@src/providers/OrderPresetsContext';
import { Box, FormControlSizes, Grid, HStack, type ISubaccount } from '@talos/kyoko';
import { useIsMobileLayout } from '../../../hooks';
import {
  MarketCardMarketAccountButton,
  type MultilegMarketAccountTooltipGroup,
} from '../MarketCardMarketAccountButton';
import type { UnifiedLiquidityMarketTooltipGroup } from '../marketCardUnifiedLiquidityTooltip';
import { getMarketCardControlRowLayout, MARKET_CARD_CONTROL_ROW_SLOT } from './marketCardControlRowLayout';

interface CardControlRowProps {
  showMarketSelectorNavigation?: boolean;
  showMarketSelectorOnMobile?: boolean;
  isEditSymbolMode?: boolean;
  showOrderPresets: boolean;
  showSubAccountSelector: boolean;
  onMarketSelectorClick?: () => void;
  aggregationPresets: MarketSelectorPreset[];
  selectedAggregationID?: string;
  onChangeAggregation: (aggregationID: string) => void;
  marketSelectorButtonLabel: string;
  marketSelectorTourDataTestId?: string;
  selectedMarketOrAccountNames: string[];
  unifiedLiquidityTooltipGroups?: UnifiedLiquidityMarketTooltipGroup[];
  multilegMarketAccountTooltipGroups?: MultilegMarketAccountTooltipGroup[];
  orderPresetSelection: IOrderPreset | undefined;
  onChangeOrderPreset: (preset?: IOrderPreset) => void;
  onToggleFavoritePreset: (orderPreset: IOrderPreset) => void;
  orderPresetOptions: IOrderPreset[];
  subAccountSelection: ISubaccount | undefined;
  subAccountOptions: ISubaccount[];
  onSubAccountChange: (subAccount?: ISubaccount) => void;
}

/** Order preset, market selector and sub account controls shared by market data and prediction market cards. */
export function CardControlRow({
  showMarketSelectorNavigation = true,
  showMarketSelectorOnMobile = false,
  isEditSymbolMode = false,
  showOrderPresets,
  showSubAccountSelector,
  onMarketSelectorClick,
  aggregationPresets,
  selectedAggregationID,
  onChangeAggregation,
  marketSelectorButtonLabel,
  marketSelectorTourDataTestId,
  selectedMarketOrAccountNames,
  unifiedLiquidityTooltipGroups,
  multilegMarketAccountTooltipGroups,
  orderPresetSelection,
  onChangeOrderPreset,
  onToggleFavoritePreset,
  orderPresetOptions,
  subAccountSelection,
  subAccountOptions,
  onSubAccountChange,
}: CardControlRowProps) {
  const isInMobileMode = useIsMobileLayout();
  const controlRowLayout = getMarketCardControlRowLayout({
    showOrderPresets,
    showSubAccountSelector,
    showMarketSelectorButton: (showMarketSelectorOnMobile || !isInMobileMode) && onMarketSelectorClick != null,
  });

  if (!controlRowLayout.slots.length) {
    return null;
  }

  return (
    <Grid gridTemplateColumns={controlRowLayout.gridTemplateColumns} gap="spacing4" data-testid="card-control-row">
      {controlRowLayout.slots.map(slot => {
        if (slot === MARKET_CARD_CONTROL_ROW_SLOT.Preset) {
          return (
            <Box key={MARKET_CARD_CONTROL_ROW_SLOT.Preset}>
              <OrderPresetSearchSelect
                selection={orderPresetSelection}
                onChange={onChangeOrderPreset}
                size={FormControlSizes.Tiny}
                showNavigation
                centered
                showChevron={false}
                options={orderPresetOptions}
                id="order-preset-selector"
                data-testid="order-preset-selector"
                onToggleFavorite={onToggleFavoritePreset}
              />
            </Box>
          );
        }

        if (slot === MARKET_CARD_CONTROL_ROW_SLOT.MarketSelector && onMarketSelectorClick) {
          return (
            <HStack key={MARKET_CARD_CONTROL_ROW_SLOT.MarketSelector} w="100%">
              <Box flex="1" data-testid={marketSelectorTourDataTestId}>
                <MarketCardMarketAccountButton
                  onClick={onMarketSelectorClick}
                  size={FormControlSizes.Tiny}
                  viewMode={controlRowLayout.marketSelectorButtonViewMode}
                  showAggregationNavigation={showMarketSelectorNavigation}
                  isEditSymbolMode={isEditSymbolMode}
                  label={marketSelectorButtonLabel}
                  selectedMarketOrAccountNames={selectedMarketOrAccountNames}
                  unifiedLiquidityTooltipGroups={unifiedLiquidityTooltipGroups}
                  multilegMarketAccountTooltipGroups={multilegMarketAccountTooltipGroups}
                  selectedAggregationID={selectedAggregationID}
                  aggregationPresets={aggregationPresets}
                  onChangeAggregation={onChangeAggregation}
                />
              </Box>
            </HStack>
          );
        }

        if (slot === MARKET_CARD_CONTROL_ROW_SLOT.SubAccount) {
          return (
            <Box key={MARKET_CARD_CONTROL_ROW_SLOT.SubAccount}>
              <SubAccountSearchSelect
                selection={subAccountSelection}
                size={FormControlSizes.Tiny}
                showNavigation
                centered
                showChevron={false}
                options={subAccountOptions}
                onChange={onSubAccountChange}
                data-testid="sub-account-selector"
              />
            </Box>
          );
        }

        return null;
      })}
    </Grid>
  );
}
